import axios from 'axios';
import classnames from 'classnames';
import React from 'react';

import { ArrowLeft, ArrowRight } from '@vimeo/bokeh-icons';
import { Button, Path, Select, Checkbox } from '@vimeo/iris/components';
import { Header, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import { NoticeWrapper, NoticeText } from 'src/_components/StyledNotice';

import exampleResponse from './_components/exampleResponse.json';
import { getFeature, hasFeature } from './_components/Features';
import Operation from './_components/Operation';
import Parameters from './_components/Parameters';
import styles from './_components/ReferenceGroup.module.scss';
import TableOfContents from './_components/TableOfContents';

class ReferenceResources extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentOperation: null,
      exampleSchemas: exampleResponse.loading,
      makingRequest: false,
      playground: null,
      playgroundResponse: null,
      showPlayground: false,
    };

    this.callApi = this.callApi.bind(this);
    this.closeOperation = this.closeOperation.bind(this);
    this.alignPlayground = this.alignPlayground.bind(this);
    this.getTryItOutButton = this.getTryItOutButton.bind(this);
    this.flattenSchemas = this.flattenSchemas.bind(this);
    this.togglePlayground = this.togglePlayground.bind(this);
    this.revealOperation = this.revealOperation.bind(this);
  }

  componentDidMount() {
    this.flattenSchemas();
  }

  flattenSchemas() {
    axios
      .get(`/api/schemas/example/${devsite.reference.group}/${devsite.reference.version}`)
      .then((res) => {
        this.setState({ exampleSchemas: { status: 'success', data: res.data } });
      })
      .catch(() => {
        this.setState({ exampleSchemas: exampleResponse.error });
      });
  }

  callApi(e) {
    e.preventDefault();

    this.setState({ makingRequest: true });

    const playground = document.getElementById('playground');
    const group = playground.querySelector('input[name=group]').value;
    const operationId = playground.querySelector('input[name=operation_id]').value;
    const queryParams = playground.getElementsByClassName('param');
    const payloadParams = playground.getElementsByClassName('bodyparam');
    const segments = playground.getElementsByClassName('segment');

    axios
      .post('/api/playground/callable', {
        headers: {
          'Content-type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        ptoken: playground.querySelector('input[name=playground_token]').value,
        token: devsite.xsrft,
        group: group,
        operation_id: operationId,
        app: document.querySelector('select[name=app]').value,
        query_params: JSON.stringify(this.buildParameterJson(queryParams)),
        payload_params: JSON.stringify(this.buildParameterJson(payloadParams)),
        segments: JSON.stringify(this.buildParameterJson(segments)),
        authenticated:
          playground.querySelector('input[name=authenticate]') !== null
            ? playground.querySelector('input[name=authenticate]').checked
            : false,
      })
      .then((res) => {
        let data = res.data;
        let req = {
          url: data.url,
          method: data.method,
          headers: data.headers,

          // Avoid any potential issues with unknown redirects and don't follow them.
          maxRedirects: 0,
        };

        if (data.payload != null) {
          req.data = data.payload;
        }

        if (data.query != null) {
          req.params = data.query;
        }

        return axios(req);
      })
      .then((res) => {
        let headers = [];
        Object.keys(res.headers).map(function (header) {
          header =
            header.toLowerCase().replace(/^.|-./g, function (str) {
              return str.toUpperCase();
            }) +
            ': ' +
            res.headers[header];

          headers.push(header);
        });

        headers.sort();

        this.setState({
          playgroundResponse: {
            headers: headers,
            code: res.status,
            body: res.data,
          },
          makingRequest: false,
        });
      })
      .catch((err) => {
        let data;
        if (err.response.config.url === '/api/playground/callable') {
          data = err.response.data.error;
        } else {
          data = err.response.data;
        }

        this.setState({
          playgroundResponse: {
            headers: [
              'HTTP/1.1 ' + err.response.status,
              'Content-Type: ' + err.response.headers['content-type'],
            ],
            code: err.response.status,
            body: data,
          },
          makingRequest: false,
        });
      });
  }

  alignPlayground(operationId) {
    let elemHeight = document.getElementById(operationId);
    let breadCrumbBottom = document.getElementById('breadcrumb');

    if (elemHeight != null) {
      let playgroundWrapper = document.getElementById('playground_wrapper');
      playgroundWrapper.style.top =
        elemHeight.offsetTop - breadCrumbBottom.offsetTop - breadCrumbBottom.offsetHeight + 'px';
      playgroundWrapper.style.position = 'relative';
    }
  }

  buildParameterJson(elements) {
    let params = {};

    Object.keys(elements).map(function (i) {
      if (elements[i].name !== '' && elements[i].value !== '') {
        if (elements[i].placeholder === 'Boolean') {
          params[elements[i].name] = elements[i].value === 'true';
        } else {
          params[elements[i].name] = elements[i].value;
        }
      }
    });

    return params;
  }

  closeOperation(operationId) {
    if (operationId === this.state.currentOperation || '#' + operationId === window.location.hash) {
      this.setState({
        currentOperation: null,
        playground: null,
      });
    }
  }

  togglePlayground() {
    this.setState({ showPlayground: !this.state.showPlayground });
  }

  fillPlayground(path, method, specification) {
    this.alignPlayground(specification.operationId);

    return (
      <div id="playground">
        <CodeBlock endpoint={{ method: method, httpPrefix: false, resources: [path] }} />

        <Parameters className={styles.params} isPlayground specification={specification} />

        {devsite?.user && (
          <Checkbox
            label={'Authenticate this call as ' + devsite?.user?.name}
            name="authenticate"
            defaultChecked="true"
            className={styles.checkbox}
          />
        )}

        <input type="hidden" name="playground_token" value={specification['x-playground-token']} />
        <input type="hidden" name="group" value={devsite?.reference.group} />
        <input type="hidden" name="operation_id" value={specification['operationId']} />
      </div>
    );
  }

  getTryItOutButton(methodSpec, path, method) {
    return (
      this.state.currentOperation !== methodSpec.operationId && (
        <a href={'#' + methodSpec.operationId}>
          <Button
            style={{ float: 'right' }}
            autoWidth="xs"
            size="xs"
            isButtonelement={'h3'}
            onClick={() => this.revealOperation(methodSpec.operationId, path, method, methodSpec)}
          >
            Try it out
          </Button>
        </a>
      )
    );
  }

  revealOperation(operationId, path, method, spec) {
    this.setState({
      currentOperation: operationId,
      playground: this.fillPlayground(path, method, spec),
      playgroundResponse: null,
      showPlayground: true,
    });
    document.getElementById('playground_column')?.classList.remove(styles.collapsed_playground);
  }

  render() {
    const groupLabel = devsite?.reference?.group_label;
    const group = devsite?.reference?.group;
    const paths = devsite?.reference?.openapi?.paths;
    const { version } = this.props;
    const { currentOperation, exampleSchemas, playground, playgroundResponse, showPlayground } =
      this.state;
    const { callApi, getTryItOutButton, revealOperation, closeOperation } = this;
    const playgroundClass = classnames(styles.playground_content, styles.playground, {
      [styles.collapsed_playground]: !showPlayground,
    });
    return (
      <>
        {version ? (
          <Meta
            title={`Vimeo API Version ${version} Reference: ${groupLabel}`}
            description={`Get a complete list of endpoints, methods, parameters, and status codes for ${groupLabel} in the Vimeo API Version ${version}.`}
          />
        ) : (
          <Meta
            title={`Vimeo API Reference: ${groupLabel}`}
            description={`Get a complete list of endpoints, methods, parameters, and status codes for ${groupLabel} in the Vimeo API.`}
          />
        )}
        <GuideWrapper
          breadcrumbs={
            <Path>
              <Path.Link href={'/'}>Home</Path.Link>
              <Path.Link href={'/apps'}>API</Path.Link>
              <Path.Link href={'/api/reference'}>API Reference</Path.Link>
              <Path.Current>{groupLabel}</Path.Current>
            </Path>
          }
          nav={{ page: 'api-reference' }}
          showContextualMenu={false}
          contentStyle={styles.main_content}
        >
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flex: '1 0 0', paddingRight: '2rem' }}>
              <Header>{groupLabel}</Header>
              {hasFeature(group) ? (
                <div>{getFeature(group)}</div>
              ) : (
                <div>
                  <Paragraph size="2">Use the API to complete the following tasks:</Paragraph>
                </div>
              )}

              {<TableOfContents paths={paths} />}

              {paths &&
                Object.keys(paths).map(function (group, i) {
                  const pathSpec = paths[group];

                  // Alphabetize path operations by the summary label.
                  let sortedOperations = {};
                  Object.keys(pathSpec.paths).map(function (path) {
                    const methods = pathSpec.paths[path];

                    Object.keys(methods).map(function (method) {
                      const summary = methods[method].summary;

                      sortedOperations[summary] = {
                        method: method,
                        path: path,
                        spec: methods[method],
                      };
                    });
                  });

                  return (
                    <div key={i}>
                      <Header size="2" id={group} key={i} className={styles.group_header}>
                        {pathSpec.info['x-devsite-label']}
                      </Header>
                      {typeof pathSpec.info['x-devsite-label-description'] !== 'undefined' && (
                        <NoticeWrapper format="primary" icon={null}>
                          <NoticeText>{pathSpec.info['x-devsite-label-description']}</NoticeText>
                        </NoticeWrapper>
                      )}

                      {Object.keys(sortedOperations)
                        .sort()
                        .map(function (ii) {
                          const operation = sortedOperations[ii];

                          return (
                            <Operation
                              key={ii}
                              currentOperation={currentOperation}
                              method={operation.method}
                              onClickOpen={() =>
                                revealOperation(
                                  operation.spec.operationId,
                                  operation.path,
                                  operation.method,
                                  operation.spec,
                                )
                              }
                              onClickClose={() => closeOperation(operation.spec.operationId)}
                              path={operation.path}
                              playgroundButton={getTryItOutButton(
                                operation.spec,
                                operation.path,
                                operation.method,
                              )}
                              schemas={exampleSchemas}
                              spec={operation.spec}
                            />
                          );
                        })}
                    </div>
                  );
                })}
            </div>

            <div id="playground_column" className={playgroundClass}>
              <div id="playground_wrapper" className={styles.playground_wrapper}>
                {this.state.showPlayground ? (
                  <div className={styles.app_selection_wrapper}>
                    <ArrowRight
                      className={styles.playground_toggle}
                      onClick={() => this.togglePlayground()}
                    />
                    <Header size="4">Test your app</Header>
                    <span className={styles.app_selection}>
                      <Select name="app">
                        {devsite?.apps &&
                          devsite.apps.map((app) => (
                            <Select.Option key={app.id} value={app.id}>
                              {app.name}
                            </Select.Option>
                          ))}
                      </Select>
                    </span>
                  </div>
                ) : (
                  <ArrowLeft
                    className={styles.expand_arrow}
                    onClick={() => this.togglePlayground()}
                  />
                )}

                {this.state.showPlayground && (
                  <div className={styles.playground_form}>
                    {playground}
                    {playground && (
                      <div style={{ paddingTop: '1rem' }}>
                        <Button
                          format="primary"
                          loading={this.state.makingRequest}
                          onClick={(e) => callApi(e)}
                          fluid
                        >
                          Make call
                        </Button>
                        <div>
                          {playgroundResponse && (
                            <CodeBlock language="json" headers={playgroundResponse.headers}>
                              {playgroundResponse.body}
                            </CodeBlock>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </GuideWrapper>
      </>
    );
  }
}

export default ReferenceResources;
